import { FormikProps } from 'formik';
import { Field } from 'types/api/Tenant/AROKMS/DisplayTableTypes';
import { Stack, TextField, Tooltip, Typography } from '@mui/material';
import { convertRuleTypeParamsToSnakeCase } from 'utils/String';
import { useEffect, memo, useMemo } from 'react';
import HelpOutlined from '@mui/icons-material/HelpOutlined';
import { useGetFieldFunctionValue } from 'services/v1/Tenant/AROKMS/DisplayTableService';
import { RULES_CONSTANT } from 'constant/RuleConstant';
import {  getErrorMessageObject } from 'utils/Error';
interface IProps {
  formik: FormikProps<{ [key: string]: string }>;
  field: Field;
  ruleTypeCode: string;
  required?: boolean;
  isEdit?: boolean;
}

const WIDTH_LABEL = '40%';

const INPUT_EDIT_MESSAGE = "Once you've submitted, this field can no longer be changed";

function getReferenceColumnNames(field: Field) {
  if (field.ruleTypeCode === RULES_CONSTANT.FUNCTION.RULE_SCORE_MAPPING) {
    const params = field.ruleTypeParams.split(',');
    const columnNamesParams: string = [params[2], params[3]].join(',');
    return convertRuleTypeParamsToSnakeCase(columnNamesParams);
  }

  return convertRuleTypeParamsToSnakeCase(field.ruleTypeParams);
}

export function SystemGeneratedFunctionTextInputComponent(props: IProps) {
  const { formik, field, required, isEdit = false } = props;
  const referenceColumns = useMemo(() => getReferenceColumnNames(field), [field]);
  const { mutateAsync: getFieldFunctionValue } = useGetFieldFunctionValue();

  // build a stable array of reference values to watch
  const referenceValues = referenceColumns.map((col) => formik.values[col]);

  useEffect(() => {
    // check if all reference values are present first
    const isAllReferenceValuePresent = referenceColumns.every((col) => {
      const value = formik.values[col];
      return value !== '' && value !== null && value !== undefined;
    });

    if (!isAllReferenceValuePresent) return;

    const tableDefinitionId = field.tableDefinitionId;

    const valueReferences: { [key: string]: any } = {};
    referenceColumns.forEach((col) => {
      valueReferences[col] = formik.values[col];
    });

    getFieldFunctionValue({ tableDefinitionId, valueReferences })
      .then((response) => {
        const functionValue = response.data?.[field.name] || '';

        // avoid extra rerenders if the value is already the same
          if (functionValue !== '' && functionValue !== formik.values[field.name])
           {
          formik.setFieldValue(field.name, functionValue);
        }
  
      })
      .catch((error) => {
      
      
        formik.setFieldError(field.name, getErrorMessageObject(error).content);
        formik.setFieldTouched(field.name, true);
      });
    // we depend on the actual primitive values + table + field name
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFieldFunctionValue, field.tableDefinitionId, field.name, referenceColumns, ...referenceValues]);

  return (
    <Stack direction='row' justifyContent='space-between' alignItems='center' marginTop={2.5} gap={1}>
      <Stack width={WIDTH_LABEL}>
        <Stack direction='row' alignItems='center' gap={0.3}>
          <Typography variant='input-label' fontWeight={700}>
            {field.label}
            {required && !field.disabled && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
          </Typography>
          {field.description && (
            <Tooltip title={field.description}>
              <HelpOutlined sx={{ fontSize: '14px' }} />
            </Tooltip>
          )}
        </Stack>
        {field.columnEditable === false && !isEdit && (
          <Typography variant='input-label-gray' fontSize={12}>
            {INPUT_EDIT_MESSAGE}
          </Typography>
        )}
      </Stack>
      <TextField
        name={field.name}
        value={formik.values[field.name]}
        disabled={field.disabled}
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        error={!!formik.touched[field.name] && !!formik.errors[field.name]}
        helperText={formik.touched[field.name] && formik.errors[field.name]}
        sx={{ width: '65%' }}
        hiddenLabel
        variant='outlined'
        size='small'
        placeholder={field.placeholder}
      />
    </Stack>
  );
}

export const SystemGeneratedFunctionTextInput = memo(SystemGeneratedFunctionTextInputComponent);
